import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Package,
  RefreshCw,
  ExternalLink,
  Star,
  Calendar,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatPrice(price) {
  if (price === null || price === undefined) return "N/A";
  return `₹${price.toLocaleString("en-IN")}`;
}

function PriceChangeCard({ label, value, icon: Icon }) {
  const isDown = value !== null && value < 0;
  const isUp = value !== null && value > 0;

  return (
    <div className="bg-[#f2f0ec] rounded-lg p-4 border border-[#e0dbd2]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#8a8278] font-semibold uppercase tracking-wider">{label}</span>
        <Icon className="h-4 w-4 text-[#8a8278]" />
      </div>
      <div
        className={cn(
          "text-xl font-extrabold font-mono",
          isDown && "text-[#1a7a3c]",
          isUp && "text-[#c0392b]",
          !isDown && !isUp && "text-[#8a8278]"
        )}
      >
        {value !== null && value !== undefined ? (
          <>
            {isDown && "▼ "}
            {isUp && "▲ "}
            {Math.abs(value).toFixed(1)}%
          </>
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const [productRes, historyRes] = await Promise.all([
        axios.get(`${API}/products/${id}`),
        axios.get(`${API}/products/${id}/history?days=90`),
      ]);
      setProduct(productRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching product:", error);
      if (error.response?.status === 404) {
        toast.error("Product not found");
        navigate("/products");
      } else {
        toast.error("Failed to load product");
      }
    } finally {
      setLoading(false);
    }
  };

  const syncProduct = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync/${id}`);
      setProduct(response.data);
      // Refresh history
      const historyRes = await axios.get(`${API}/products/${id}/history?days=90`);
      setHistory(historyRes.data);
      toast.success("Product synced successfully");
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error("Failed to sync product");
    } finally {
      setSyncing(false);
    }
  };

  // Prepare chart data
  const chartData = history
    .slice(-100)
    .map((h) => ({
      timestamp: h.timestamp,
      date: format(new Date(h.timestamp), "MMM d"),
      price: h.price,
    }));

  // Calculate min/max for chart
  const prices = chartData.map((d) => d.price);
  const minPrice = prices.length > 0 ? Math.min(...prices) * 0.95 : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) * 1.05 : 100;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-1" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-16">
        <Package className="h-16 w-16 mx-auto text-[#8a8278] mb-4" />
        <h2 className="text-xl font-semibold mb-2">Product not found</h2>
        <Button asChild variant="link">
          <Link to="/products">Back to products</Link>
        </Button>
      </div>
    );
  }

  const isBelowBAU = product.bau_price && product.current_price && product.current_price < product.bau_price;

  return (
    <div data-testid="product-detail-page" className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        asChild
        className="hover:bg-[#f2f0ec] -ml-2"
        data-testid="back-btn"
      >
        <Link to="/products">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Link>
      </Button>

      {/* Product Header */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Product Info Card */}
        <Card className="bg-white border-[#e0dbd2] lg:w-80 flex-shrink-0">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="w-32 h-32 rounded-lg object-cover bg-[#f2f0ec] mb-4"
                />
              ) : (
                <div className="w-32 h-32 rounded-lg bg-[#f2f0ec] flex items-center justify-center mb-4 text-4xl">
                  {product.product_type === 'own' ? '📦' : '🎯'}
                </div>
              )}

              <div className="flex gap-2 mb-3">
                <Badge
                  className={cn(
                    product.product_type === "own"
                      ? "bg-blue-100 text-blue-700 border-blue-200"
                      : "bg-amber-100 text-amber-700 border-amber-200"
                  )}
                >
                  {product.product_type === "own" ? "My Product" : "Competition"}
                </Badge>
                {isBelowBAU && (
                  <Badge className="bg-red-100 text-red-700 border-red-200">
                    Below BAU
                  </Badge>
                )}
              </div>

              <h1 className="text-lg font-extrabold text-[#1a1814] mb-2">
                {product.title || product.name || "Unnamed Product"}
              </h1>

              <p className="text-sm text-[#8a8278] font-mono mb-4">
                {product.asin}
              </p>

              {product.brand && (
                <p className="text-sm text-[#8a8278] mb-1">
                  Brand: <span className="text-[#1a1814] font-semibold">{product.brand}</span>
                </p>
              )}

              {product.category && (
                <p className="text-sm text-[#8a8278] mb-4">
                  Category: <span className="text-[#1a1814] font-semibold">{product.category}</span>
                </p>
              )}

              {product.rating && (
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  <span className="font-semibold">{product.rating.toFixed(1)}</span>
                  {product.review_count && (
                    <span className="text-[#8a8278] text-sm">
                      ({product.review_count.toLocaleString()} reviews)
                    </span>
                  )}
                </div>
              )}

              <div className="w-full border-t border-[#e0dbd2] pt-4 mt-2">
                <div className="text-3xl font-extrabold font-mono text-[#1a1814] mb-1">
                  {formatPrice(product.current_price)}
                </div>
                <p className="text-xs text-[#8a8278]">Current Price</p>
                
                {product.bau_price && (
                  <div className="mt-3 p-3 bg-[#f2f0ec] rounded-lg">
                    <div className="text-sm text-[#8a8278]">BAU Price</div>
                    <div className="text-lg font-bold font-mono">{formatPrice(product.bau_price)}</div>
                    {isBelowBAU && (
                      <div className="text-xs text-[#c0392b] font-semibold mt-1">
                        ₹{Math.abs(product.current_price - product.bau_price).toLocaleString("en-IN")} below BAU
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-6 w-full">
                <Button
                  className="flex-1 bg-[#f0a500] hover:bg-[#ffc533] text-[#1a1200] font-bold"
                  onClick={syncProduct}
                  disabled={syncing}
                  data-testid="sync-product-btn"
                >
                  <RefreshCw
                    className={cn("h-4 w-4 mr-2", syncing && "animate-spin")}
                  />
                  {syncing ? "Syncing..." : "Sync"}
                </Button>
                <Button
                  variant="outline"
                  className="border-[#e0dbd2]"
                  asChild
                  data-testid="view-amazon-btn"
                >
                  <a
                    href={`https://www.amazon.in/dp/${product.asin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price History Chart */}
        <Card className="bg-white border-[#e0dbd2] flex-1">
          <CardHeader className="border-b border-[#e0dbd2]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-extrabold">Price History</CardTitle>
              <span className="text-sm text-[#8a8278]">
                Last 90 days
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {chartData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f0a500" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f0a500" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0dbd2" />
                    <XAxis
                      dataKey="date"
                      stroke="#8a8278"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#8a8278"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      domain={[minPrice, maxPrice]}
                      tickFormatter={(value) => `₹${Math.round(value)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e0dbd2",
                        borderRadius: "8px",
                      }}
                      labelStyle={{ color: "#1a1814" }}
                      formatter={(value) => [
                        `₹${value.toLocaleString("en-IN")}`,
                        "Price",
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="price"
                      stroke="#f0a500"
                      strokeWidth={2}
                      fill="url(#priceGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#8a8278]">
                <div className="text-center">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No price history available yet</p>
                  <p className="text-sm mt-1">Sync the product to fetch historical data</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Price Change Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <PriceChangeCard
          label="24 Hour Change"
          value={product.price_change_24h}
          icon={Clock}
        />
        <PriceChangeCard
          label="7 Day Change"
          value={product.price_change_7d}
          icon={Calendar}
        />
        <PriceChangeCard
          label="30 Day Change"
          value={product.price_change_30d}
          icon={Calendar}
        />
        <div className="bg-[#f2f0ec] rounded-lg p-4 border border-[#e0dbd2]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#8a8278] font-semibold uppercase tracking-wider">Last Synced</span>
            <RefreshCw className="h-4 w-4 text-[#8a8278]" />
          </div>
          <div className="text-sm font-semibold">
            {product.last_synced
              ? format(new Date(product.last_synced), "MMM d, yyyy HH:mm")
              : "Never"}
          </div>
        </div>
      </div>

      {/* Additional Prices */}
      {(product.lowest_price_7d || product.lowest_price_30d) && (
        <Card className="bg-white border-[#e0dbd2]">
          <CardHeader className="border-b border-[#e0dbd2]">
            <CardTitle className="text-lg font-extrabold">Lowest Prices</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.lowest_price_7d && (
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-[#1a4a8a] mb-1">7-Day Lowest</p>
                  <p className="text-2xl font-extrabold font-mono text-[#1a4a8a]">
                    {formatPrice(product.lowest_price_7d)}
                  </p>
                  {product.current_price && product.current_price <= product.lowest_price_7d && (
                    <p className="text-xs text-[#1a7a3c] mt-1">🏆 At lowest!</p>
                  )}
                </div>
              )}
              {product.lowest_price_30d && (
                <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-[#b45309] mb-1">30-Day Lowest</p>
                  <p className="text-2xl font-extrabold font-mono text-[#b45309]">
                    {formatPrice(product.lowest_price_30d)}
                  </p>
                  {product.current_price && product.current_price <= product.lowest_price_30d && (
                    <p className="text-xs text-[#1a7a3c] mt-1">🏆 At lowest!</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {product.notes && (
        <Card className="bg-white border-[#e0dbd2]">
          <CardHeader className="border-b border-[#e0dbd2]">
            <CardTitle className="text-lg font-extrabold">Notes</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-[#8a8278]">{product.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
